import React, { useState, useMemo, useRef, useEffect } from "react";
import { vacanciesApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./VacanciesInCollege.scss";

const CATEGORIES = ["Teaching", "Non-Teaching", "Administrative", "Technical", "Research", "Other"];
const JOB_TYPES = ["Full-Time", "Part-Time", "Contract", "Guest Faculty", "Temporary"];
const STATUSES = ["Active", "Closed", "Draft"];

const defaultForm = {
  _id: null,
  Job_Title: "",
  Department: "",
  Category: "Teaching",
  Job_Type: "Full-Time",
  No_of_Vacancies: 1,
  Posted_Date: new Date().toISOString().slice(0, 10),
  Last_Date_to_Apply: new Date().toISOString().slice(0, 10),
  Salary_Pay_Scale: "",
  Status: "Active",
  Pin_to_Top: false,
  Visible_on_Website: true,
  Qualification_Required: "",
  Experience_Required: "",
  Job_Description: "",
  Responsibilities: "",
  Eligibility_Criteria: "",
  Application_Email: "",
  Online_Application_URL: "",
  Tags: [],
};

const VacanciesInCollege = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(""); // "add", "edit", "view", "delete"
  const [formData, setFormData] = useState(defaultForm);
  const [confirmId, setConfirmId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [activeTab, setActiveTab] = useState("info"); // For form tabs
  const [viewItem, setViewItem] = useState(null);
  const [activeDept, setActiveDept] = useState("All");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchVacancies(); }, []);

  const fetchVacancies = async () => {
    try {
      setLoading(true);
      const response = await apiClient(vacanciesApi);
      const result = await response.json();
      if (response.ok) { setVacancies(result.data || []); }
    } catch (error) {
      console.error("Error fetching vacancies:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const departments = useMemo(() => ["All", ...new Set(vacancies.map(v => v.Department))].sort(), [vacancies]);

  const filtered = useMemo(() => {
    return vacancies.filter((v) => {
      const matchSearch = (v.Job_Title || "").toLowerCase().includes(searchText.toLowerCase()) || (v.Department || "").toLowerCase().includes(searchText.toLowerCase());
      const matchDept = activeDept === "All" || v.Department === activeDept;
      return matchSearch && matchDept;
    });
  }, [vacancies, searchText, activeDept]);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 210 });
    setDropdownOpenId(id);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? vacanciesApi : `${vacanciesApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        fetchVacancies();
        setModalType("");
      }
    } catch (error) {
      console.error("Error saving vacancy:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`${vacanciesApi}/${confirmId}`, { method: "DELETE" });
      if (response.ok) {
        fetchVacancies();
        setConfirmId(null);
        setModalType("");
      }
    } catch (error) {
      console.error("Error deleting vacancy:", error);
    } finally {
      setLoading(false);
    }
  };

  const toDateStr = (d) => d ? new Date(d).toISOString().split('T')[0] : "";
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const stats = {
    total: vacancies.length,
    active: vacancies.filter(v => v.Status === "Active").length,
    closed: vacancies.filter(v => v.Status === "Closed").length,
    teaching: vacancies.filter(v => v.Category === "Teaching").length
  };

  return (
    <div className="vc-page">
      <div className="vc-header">
        <div className="vc-header-left">
          <div className="vc-header-icon">💼</div>
          <div>
            <h1>Vacancies in College</h1>
            <p>Manage job openings and faculty recruitment postings for Rajat Group Of College.</p>
          </div>
        </div>
        <div className="vc-header-actions">
          <button className="vc-btn-primary" onClick={() => { setFormData(defaultForm); setModalType("add"); setActiveTab("info"); }}>+ Add New Vacancy</button>
        </div>
      </div>

      <div className="vc-stats-row">
        <div className="vc-stat-card">
          <div className="vc-stat-icon">📄</div>
          <div><strong>{stats.total}</strong><p>Total Postings</p></div>
        </div>
        <div className="vc-stat-card" style={{ borderTopColor: "#10b981" }}>
          <div className="vc-stat-icon">✅</div>
          <div><strong>{stats.active}</strong><p>Active Openings</p></div>
        </div>
        <div className="vc-stat-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="vc-stat-icon">🚫</div>
          <div><strong>{stats.closed}</strong><p>Closed Vacancies</p></div>
        </div>
        <div className="vc-stat-card" style={{ borderTopColor: "#3b82f6" }}>
          <div className="vc-stat-icon">🎓</div>
          <div><strong>{stats.teaching}</strong><p>Teaching Staff</p></div>
        </div>
      </div>

      <div className="vc-filters">
        <div className="vc-filters-top">
          <div className="vc-search">
            <input type="text" placeholder="Search by title or department..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            {searchText && <button className="vc-clr" onClick={() => setSearchText("")}>×</button>}
          </div>
          <div className="vc-dept-tabs">
            {departments.slice(0, 8).map(dept => (
              <button key={dept} className={`vc-dept-tab ${activeDept === dept ? "active" : ""}`} onClick={() => setActiveDept(dept)}>
                {dept}
                <span className="vc-dept-count">{dept === "All" ? vacancies.length : vacancies.filter(v => v.Department === dept).length}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="vc-grid">
        {filtered.length > 0 ? filtered.map((item) => {
          const isExpired = item.Last_Date_to_Apply && new Date(item.Last_Date_to_Apply) < new Date();
          return (
            <div key={item._id} className={`vc-card ${!item.Visible_on_Website ? "hidden-card" : ""} ${item.Pin_to_Top ? "pinned-card" : ""}`}>
              <div className="vc-card-head">
                <div className="vc-card-title-row">
                  <h3 onClick={() => { setViewItem(item); setModalType("view"); }}>{item.Job_Title}</h3>
                  <div className="vc-card-badges">
                    {item.Pin_to_Top && <span className="vc-badge-small pin">📌</span>}
                    {!item.Visible_on_Website && <span className="vc-badge-small hidden">👁️</span>}
                    <button className="vc-dots" onClick={(e) => handleDotsClick(e, item._id)}>⋮</button>
                  </div>
                </div>
                <div className="vc-card-meta-row">
                  <span>🏢 {item.Department}</span>
                  <span className="vc-job-type-tag">{item.Job_Type}</span>
                </div>
              </div>
              <div className="vc-card-details">
                <div className="vc-detail-chip"><span>👥</span><p>{item.No_of_Vacancies} Positions</p></div>
                <div className="vc-detail-chip"><span>📅</span><p>Till {formatDate(item.Last_Date_to_Apply)}</p></div>
              </div>
              <p className="vc-card-desc">{item.Qualification_Required}</p>
              <div className="vc-card-footer">
                <div className="vc-card-footer-left">
                  <span className={`vc-status-badge ${item.Status.toLowerCase()}`}>
                    <div className="vc-status-dot" style={{ background: item.Status === 'Active' ? '#10b981' : item.Status === 'Closed' ? '#ef4444' : '#64748b' }}></div>
                    {item.Status}
                  </span>
                  {isExpired && item.Status === 'Active' && <span className="vc-deadline expired">⚠️ Expired</span>}
                </div>
                <button className="vc-view-btn-sm" onClick={() => { setViewItem(item); setModalType("view"); }}>View Details</button>
              </div>
            </div>
          );
        }) : (
          <div className="vc-empty" style={{ gridColumn: '1/-1' }}>
            <div>📭</div>
            <h3>No Vacancies Found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        )}
      </div>

      {dropdownOpenId && (
        <div ref={dropdownRef} className="vc-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <div className="vc-dd-label">Actions</div>
          <button onClick={() => {
            const v = vacancies.find(x => x._id === dropdownOpenId);
            setFormData({ ...v, Posted_Date: toDateStr(v.Posted_Date), Last_Date_to_Apply: toDateStr(v.Last_Date_to_Apply) });
            setModalType("edit");
            setActiveTab("info");
            setDropdownOpenId(null);
          }}>✏️ Edit Details</button>
          <button onClick={() => {
            const v = vacancies.find(x => x._id === dropdownOpenId);
            setFormData({ ...v, Posted_Date: toDateStr(v.Posted_Date), Last_Date_to_Apply: toDateStr(v.Last_Date_to_Apply) });
            setModalType("edit");
            setActiveTab("details");
            setDropdownOpenId(null);
          }}>🌐 Visibility Settings</button>
          <div className="vc-dd-divider"></div>
          <button className="vc-dd-del" onClick={() => { setConfirmId(dropdownOpenId); setModalType("delete"); setDropdownOpenId(null); }}>🗑️ Delete Permanent</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="vc-overlay" onClick={() => setModalType("")}>
          <div className="vc-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vc-modal-head">
              <div>
                <h2>{modalType === "add" ? "Add New" : "Edit"} Vacancy</h2>
                <p>Enter the job details and requirements for the recruitment posting.</p>
              </div>
              <button className="vc-modal-close" onClick={() => setModalType("")}>×</button>
            </div>
            <div className="vc-form-tabs">
              <button className={`vc-form-tab ${activeTab === "info" ? "active" : ""}`} onClick={() => setActiveTab("info")}>Basic Info</button>
              <button className={`vc-form-tab ${activeTab === "requirements" ? "active" : ""}`} onClick={() => setActiveTab("requirements")}>Requirements</button>
              <button className={`vc-form-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")}>Details & Visibility</button>
            </div>
            <form className="vc-form" onSubmit={handleSave}>
              {activeTab === "info" && (
                <div className="vc-form-section">
                  <div className="vc-field full"><label>Job Title <span className="req">*</span></label><input placeholder="e.g. Assistant Professor - Computer Science" value={formData.Job_Title} onChange={(e) => setFormData({ ...formData, Job_Title: e.target.value })} required /></div>
                  <div className="vc-field-row">
                    <div className="vc-field"><label>Department <span className="req">*</span></label><input placeholder="e.g. Information Technology" value={formData.Department} onChange={(e) => setFormData({ ...formData, Department: e.target.value })} required /></div>
                    <div className="vc-field">
                      <label>Category <span className="req">*</span></label>
                      <select value={formData.Category} onChange={(e) => setFormData({ ...formData, Category: e.target.value })}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="vc-field-row">
                    <div className="vc-field">
                      <label>Job Type <span className="req">*</span></label>
                      <select value={formData.Job_Type} onChange={(e) => setFormData({ ...formData, Job_Type: e.target.value })}>
                        {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="vc-field"><label>Number of Vacancies <span className="req">*</span></label><input type="number" min="1" value={formData.No_of_Vacancies} onChange={(e) => setFormData({ ...formData, No_of_Vacancies: e.target.value })} required /></div>
                  </div>
                  <div className="vc-field-row">
                    <div className="vc-field"><label>Posted Date</label><input type="date" value={formData.Posted_Date} onChange={(e) => setFormData({ ...formData, Posted_Date: e.target.value })} /></div>
                    <div className="vc-field"><label>Last Date to Apply <span className="req">*</span></label><input type="date" value={formData.Last_Date_to_Apply} onChange={(e) => setFormData({ ...formData, Last_Date_to_Apply: e.target.value })} required /></div>
                  </div>
                </div>
              )}
              {activeTab === "requirements" && (
                <div className="vc-form-section">
                  <div className="vc-field full"><label>Qualification Required <span className="req">*</span></label><textarea placeholder="Specify required degrees and certifications..." value={formData.Qualification_Required} onChange={(e) => setFormData({ ...formData, Qualification_Required: e.target.value })} rows="3" required /></div>
                  <div className="vc-field full"><label>Experience Required</label><textarea placeholder="Specify years of experience and domain expertise..." value={formData.Experience_Required} onChange={(e) => setFormData({ ...formData, Experience_Required: e.target.value })} rows="2" /></div>
                  <div className="vc-field full"><label>Eligibility & Criteria</label><textarea placeholder="Other eligibility conditions..." value={formData.Eligibility_Criteria} onChange={(e) => setFormData({ ...formData, Eligibility_Criteria: e.target.value })} rows="3" /></div>
                </div>
              )}
              {activeTab === "details" && (
                <div className="vc-form-section">
                  <div className="vc-field full"><label>Job Description</label><textarea placeholder="Brief summary of the role and duties..." value={formData.Job_Description} onChange={(e) => setFormData({ ...formData, Job_Description: e.target.value })} rows="3" /></div>
                  <div className="vc-field-row">
                    <div className="vc-field"><label>Salary/Pay Scale</label><input placeholder="e.g. Level 10 (7th CPC)" value={formData.Salary_Pay_Scale} onChange={(e) => setFormData({ ...formData, Salary_Pay_Scale: e.target.value })} /></div>
                    <div className="vc-field">
                      <label>Current Status</label>
                      <select value={formData.Status} onChange={(e) => setFormData({ ...formData, Status: e.target.value })}>
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="vc-field-row">
                    <div className="vc-field"><label>Application Email</label><input placeholder="recruitment@college.com" value={formData.Application_Email} onChange={(e) => setFormData({ ...formData, Application_Email: e.target.value })} /></div>
                    <div className="vc-field"><label>Online Apply URL</label><input placeholder="https://college.com/apply" value={formData.Online_Application_URL} onChange={(e) => setFormData({ ...formData, Online_Application_URL: e.target.value })} /></div>
                  </div>
                  <div className="vc-toggle-row">
                    <div className="vc-toggle-checks">
                      <label className="vc-toggle-item">
                        <input type="checkbox" checked={formData.Pin_to_Top} onChange={(e) => setFormData({ ...formData, Pin_to_Top: e.target.checked })} />
                        <div className="vc-chk-box"></div>
                        <div><strong>Pin to Top</strong><p>Featured jobs will stay at the top of the list.</p></div>
                      </label>
                      <label className="vc-toggle-item">
                        <input type="checkbox" checked={formData.Visible_on_Website} onChange={(e) => setFormData({ ...formData, Visible_on_Website: e.target.checked })} />
                        <div className="vc-chk-box"></div>
                        <div><strong>Visible on Website</strong><p>Toggle public visibility for this posting.</p></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <div className="vc-form-footer">
                <div className="vc-form-tab-nav">
                  {activeTab !== "info" && <button type="button" className="vc-btn-light" onClick={() => setActiveTab(activeTab === "details" ? "requirements" : "info")}>← Back</button>}
                  {activeTab !== "details" && <button type="button" className="vc-btn-light" onClick={() => setActiveTab(activeTab === "info" ? "requirements" : "details")}>Next →</button>}
                </div>
                <div className="vc-form-footer-right">
                  <button type="button" className="vc-btn-light" onClick={() => setModalType("")}>Cancel</button>
                  <button type="submit" className="vc-btn-primary">Save Changes</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && viewItem && (
        <div className="vc-overlay" onClick={() => setModalType("")}>
          <div className="vc-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="vc-modal-close abs" onClick={() => setModalType("")}>×</button>
            <div className="vc-view-head">
              <div className="vc-view-head-left">
                <div className="vc-view-dept-icon">🏢</div>
                <div>
                  <div className="vc-view-meta-row">
                    <span className="vc-cat-pill" style={{ background: '#f0fdf4', color: '#059669', border: '1px solid #a7f3d0' }}>{viewItem.Category}</span>
                    <span className={`vc-status-badge ${viewItem.Status.toLowerCase()}`}>{viewItem.Status}</span>
                  </div>
                  <h2>{viewItem.Job_Title}</h2>
                  <div className="vc-view-sub-meta">
                    <span>🏢 {viewItem.Department}</span>
                    <span>⏳ Type: {viewItem.Job_Type}</span>
                    <span>👥 {viewItem.No_of_Vacancies} Positions</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="vc-view-body">
              <div className={`vc-deadline-banner ${new Date(viewItem.Last_Date_to_Apply) < new Date() ? 'expired' : ''}`}>
                <span>📅</span>
                <div>
                  <strong>Deadline: {formatDate(viewItem.Last_Date_to_Apply)}</strong>
                  <p>{new Date(viewItem.Last_Date_to_Apply) < new Date() ? "Application period has ended." : "Make sure to apply before the deadline."}</p>
                </div>
              </div>
              <div className="vc-view-details-grid">
                <div className="vc-detail-item"><span>SALARY</span><strong>{viewItem.Salary_Pay_Scale || "Not Specified"}</strong></div>
                <div className="vc-detail-item"><span>POSTED ON</span><strong>{formatDate(viewItem.Posted_Date)}</strong></div>
              </div>
              <div className="vc-view-section">
                <h4>Qualification Required</h4>
                <p>{viewItem.Qualification_Required}</p>
              </div>
              {viewItem.Experience_Required && (
                <div className="vc-view-section">
                  <h4>Experience</h4>
                  <p>{viewItem.Experience_Required}</p>
                </div>
              )}
              {viewItem.Job_Description && (
                <div className="vc-view-section">
                  <h4>Job Description</h4>
                  <p>{viewItem.Job_Description}</p>
                </div>
              )}
              <div className="vc-view-apply-box">
                <h4>How to Apply</h4>
                <p>{viewItem.Application_Email ? `Send your resume to: ${viewItem.Application_Email}` : "Please follow the online application link below."}</p>
                {viewItem.Online_Application_URL && <a href={viewItem.Online_Application_URL} target="_blank" rel="noreferrer" className="vc-apply-link">Apply Online Portal 🔗</a>}
              </div>
              <div className="vc-view-actions">
                <button className="vc-btn-primary" onClick={() => {
                  setFormData({ ...viewItem, Posted_Date: toDateStr(viewItem.Posted_Date), Last_Date_to_Apply: toDateStr(viewItem.Last_Date_to_Apply) });
                  setModalType("edit");
                  setActiveTab("info");
                }}>Edit Posting</button>
                <button className="vc-btn-secondary" onClick={() => setModalType("")}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalType === "delete" && (
        <div className="vc-overlay" onClick={() => setModalType("")}>
          <div className="vc-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="vc-confirm-icon">⚠️</div>
            <h3>Delete Vacancy?</h3>
            <p>This action cannot be undone. This job posting will be removed from both admin and website.</p>
            <div className="vc-confirm-actions">
              <button className="vc-btn-light" onClick={() => setModalType("")}>Cancel</button>
              <button className="vc-btn-danger" onClick={handleDelete}>Delete Permanent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacanciesInCollege;